import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';
import { AI_SERVICE_BASE_URL } from '../AiApiService';

/**
 * GoalApi — endpoints for the self-directed goal/project plan creation flow.
 *
 *  - getIntakeFollowups + generatePlan call AI-Service (proxied through data-service).
 *  - createFromPlan calls hibernate-service to atomically persist the reviewed plan.
 */
export const GoalApi = {
  /**
   * Ask the LLM what 2-3 follow-up questions it needs answered before drafting
   * a plan for the given intake basics.
   *
   * @param {Object} basics - structured user intake (title, category, targetDate,
   *   weeklyHours, currentLevel, motivation, etc.)
   * @param {Array<number>=} documentIds - optional list of document IDs the user
   *   attached during the wizard; the server extracts events/deadlines/blackouts
   *   from them and treats them as planning constraints.
   * @returns {Promise<Response>} JSON: {
   *   questions: Array<{id, question, answer_type, choices?, rationale?}>,
   *   documentContext?: {documents, events, deadlines, blackoutDates, topics, constraints, aggregateNotes}
   * }
   */
  getIntakeFollowups: async (basics, documentIds) => {
    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/plans/intake-questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ basics, documentIds: documentIds || [] }),
    });
  },

  /**
   * Generate a full multi-month plan from the user's intake basics plus optional
   * follow-up answers and attached documents. Returns a plan body in the shape
   * consumed by createFromPlan().
   *
   * @param {Object} basics
   * @param {Array<{id, answer}>=} followupAnswers
   * @param {Array<number>=} documentIds
   * @returns {Promise<Response>} JSON plan (endeavor, summary, events, assignments, documentContext?)
   */
  generatePlan: async (basics, followupAnswers, documentIds) => {
    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/plans/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        basics,
        followupAnswers,
        documentIds: documentIds || [],
      }),
    });
  },

  /**
   * Atomically persist a reviewed plan as a self-directed endeavor.
   *
   * @param {Object} plan - matches GoalPlanRequest on hibernate-service
   * @returns {Promise<Response>} JSON: { endeavor, events, assignments, blueprintsCreated }
   */
  createFromPlan: async (plan) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/from-plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(plan),
    });
  },

  /**
   * Trigger just-in-time materialization of a content blueprint into a real
   * QuizSet / FlashcardDeck / doc. Idempotent: a blueprint already in `ready`
   * state short-circuits and returns its existing generated resource handle.
   *
   * @param {number|string} blueprintId
   * @returns {Promise<Response>} JSON: {
   *   blueprintId, eventId, contentType, status,
   *   generatedResourceKind, generatedResourceId, error
   * }
   */
  materializeBlueprint: async (blueprintId) => {
    return fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/blueprints/${blueprintId}/materialize`,
      { method: 'POST' },
    );
  },

  /**
   * Ask AI-Service to script a conversational check-in session ordering the
   * already-materialized blueprints. Caller assembles `event`, `endeavor`, and
   * `blueprints` (only ready blueprints; each needs `generatedResourceKind` +
   * `generatedResourceId`).
   *
   * @param {number|string} eventId
   * @param {{event: object, endeavor: object, blueprints: Array<object>}} context
   * @returns {Promise<Response>} JSON: {
   *   eventId, title, estimatedMinutes,
   *   steps: [ {kind, title, body, resourceRef?: {kind, id}, prompts?: [...]} ],
   *   _meta: {...}
   * }
   */
  getSessionScript: async (eventId, context) => {
    return fetchWithAuth(
      `${AI_SERVICE_BASE_URL}/api/check-ins/${eventId}/session-script`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(context || {}),
      },
    );
  },

  /**
   * Phase 5 — fetch the aggregated plan-health snapshot for the endeavor
   * (event/assignment/quiz/concept progress + derived signals).
   *
   * @param {number|string} endeavorId
   * @returns {Promise<Response>} JSON: {
   *   endeavorId, computedAt, windowDays,
   *   events: {...}, assignments: {...}, quiz: {...}, concepts: {...},
   *   signals: [...]
   * }
   */
  getPlanHealth: async (endeavorId) => {
    return fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/plan-health`,
      { method: 'GET' },
    );
  },

  /**
   * Phase 5 — ask AI-Service to propose plan adjustments. Caller forwards the
   * snapshot from `getPlanHealth` along with current events/assignments.
   *
   * @param {number|string} endeavorId
   * @param {{endeavor: object, events: Array, assignments: Array, health: object, trigger?: string}} context
   * @returns {Promise<Response>} JSON: { endeavorId, summary, trigger, adjustments: [...], _meta }
   */
  reassessPlan: async (endeavorId, context) => {
    return fetchWithAuth(
      `${AI_SERVICE_BASE_URL}/api/plans/${endeavorId}/reassess`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(context || {}),
      },
    );
  },

  /**
   * Phase 5 — apply a batch of plan adjustments (typically the body returned
   * by `reassessPlan`) to the endeavor.
   *
   * @param {number|string} endeavorId
   * @param {{trigger?: string, summary?: string, adjustments: Array}} payload
   * @returns {Promise<Response>} JSON: { endeavorId, appliedCount, skippedCount, applied: [...], skipped: [...] }
   */
  applyPlanAdjustments: async (endeavorId, payload) => {
    return fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/plan-adjustments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
      },
    );
  },
};

export default GoalApi;
